import {
  Autocomplete,
  Box,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import backArrow from "../../assets/icons/backArrow.svg";
import { CustomButton } from "../../global/components";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import strings from "../../global/constants/StringConstants";
import { handleErrorMessage } from "../../helpers/methods";
import history from "../../utils/history";
import { regularFont, theme } from "../../utils/styles";
import { getProfile } from "../Dashboard/DashboardService";
import CompatibilityPercentage from "../Shared/Compatibility/CompatibilityPercentage";
import TwoUsersDetails from "../Shared/Compatibility/TwoUsersDetails";
import compatibilityStyles from "./Compatibility.styles";
import { compatibilityForUsersProfiles } from "./CompatibilityServices";

const Compatibility = () => {
  const classes = compatibilityStyles;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const { i18n, t } = useTranslation();
  const [profile1, setProfile1] = useState("");
  const [profile2, setProfile2] = useState("");
  const [profiles, setProfiles] = useState<any[]>([]);
  const [compatibilityData, setCompatibilityData] = useState<any>(null);
  const [twoPersonCompatibilityList, setTwoPersonCompatibilityList] = useState<
    any[]
  >([]);
  const [compatibilityPercentageData, setCompatibilityPercentageData] =
    useState<any>(null);

  useEffect(() => {
    getProfiles();
  }, []);

  useEffect(() => {
    if (
      twoPersonCompatibilityList &&
      twoPersonCompatibilityList.length > 0 &&
      profile1 &&
      profile2
    ) {
      handleFetchDetails();
    }
  }, [i18n.language]);

  const getProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await getProfile();
      setProfiles(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleFetchDetails = async () => {
    try {
      setIsLoading(true);
      const language = i18n.language.toLowerCase();
      const response = await compatibilityForUsersProfiles(
        profile1,
        profile2,
        language
      );
      setCompatibilityData(response);
      setCompatibilityPercentageData({
        destiny: response.destiny,
        emotional: response.emotional,
        marriage: response.marriage,
        physical: response.physical,
        soul: response.soul,
        totalPercentage: response.totalPercentage,
      });
      setTwoPersonCompatibilityList(response.twoPersonCompatibilityList);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getColorBasedOnValue = (value: number) => {
    if (value >= 0 && value < 40) {
      return ["#FF0000", "#ffffff"];
    } else if (value >= 40 && value < 50) {
      return ["#FFFF00", "#ffffff"];
    } else if (value >= 50 && value < 70) {
      return ["#90EE90", "#ffffff"];
    } else {
      return ["#006400", "#FFFFFF"];
    }
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { sm: "center", xs: "baseline" },
          width: "100%",
        }}
        pb={3}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.goBack()}
        >
          {isMobile ? (
            <img src={backArrow} height={"20px"} alt="Module not found..." />
          ) : (
            <img src={backArrow} height={"25px"} alt="Module not found..." />
          )}
        </Box>
        <Typography sx={classes.italicTypo}>{t("compatibility")}</Typography>
      </Box>
    );
  };

  const getAutocompletes = () => {
    return (
      <Box sx={classes.autoCompleteWrapper}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={classes.autoCompleteLabel}>
            {t("profiles")}1
          </Typography>
          <Autocomplete
            sx={classes.autoComplete}
            options={profiles || []}
            getOptionLabel={(option: any) => option.name || ""}
            renderInput={(params) => (
              <TextField {...params} placeholder={t("searchProfile")} />
            )}
            onChange={(event, value) => {
              setProfile1(value?.profileId || "");
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={classes.autoCompleteLabel}>
            {t("profiles")}2
          </Typography>
          <Autocomplete
            sx={classes.autoComplete}
            options={profiles || []}
            getOptionLabel={(option: any) => option.name || ""}
            renderInput={(params) => (
              <TextField {...params} placeholder={t("searchProfile")} />
            )}
            onChange={(event, value) => {
              setProfile2(value?.profileId || "");
            }}
          />
        </Box>
      </Box>
    );
  };

  const getButton = () => {
    return (
      <Box sx={{ width: "300px", justifySelf: "center", my: 2 }}>
        <CustomButton
          label={t("generate")}
          type={strings.PRIMARY}
          onClick={handleFetchDetails}
        />
      </Box>
    );
  };
  const getTwoPersonCompatibilityList = () => {
    if (!twoPersonCompatibilityList || twoPersonCompatibilityList.length === 0)
      return null;

    // Get names from compatibilityData if available
    const person1Name =
      compatibilityData?.userDetails?.name ||
      profiles.find((p) => p.profileId === profile1)?.name ||
      "Person 1";
    const person2Name =
      compatibilityData?.targetDetails?.name ||
      profiles.find((p) => p.profileId === profile2)?.name ||
      "Person 2";

    return (
      <Box my={3} sx={{ maxWidth: "900px", margin: "0 auto" }}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={classes.namesHeader}
        >
          <Typography sx={classes.personName}>{person1Name}</Typography>
          <Typography sx={classes.vsText}>VS</Typography>
          <Typography sx={classes.personName}>{person2Name}</Typography>
        </Stack>
        <Box
          sx={{
            borderRadius: "16px",
            background: "rgba(255,255,255,0.05)",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            p: 2,
          }}
        >
          {twoPersonCompatibilityList.map((item, idx) => {
            const [barColor, textColor] = getColorBasedOnValue(
              Number(item.finalValue)
            );
            const yesValue = t("yes");
            const noValue = t("no");
            const finalValueLower = String(item.finalValue).toLowerCase();
            const yesValueLower = yesValue.toLowerCase();
            const noValueLower = noValue.toLowerCase();
            const isYesNo =
              finalValueLower === "yes" ||
              finalValueLower === "no" ||
              finalValueLower === yesValueLower ||
              finalValueLower === noValueLower;
            const isYes =
              finalValueLower === "yes" || finalValueLower === yesValueLower;
            return (
              <Stack
                key={idx}
                direction={{ sm: "row", xs: "column" }}
                alignItems="center"
                spacing={2}
                sx={classes.comparisonRow}
              >
                <Stack flex={1} spacing={0.5}>
                  <Typography
                    sx={{ ...regularFont, color: "#FFF", opacity: 0.8 }}
                  >
                    {item.attribute1}
                  </Typography>
                  <Typography sx={{ ...regularFont, color: "#FFF" }}>
                    {Array.isArray(item.value1)
                      ? item.value1.join(", ")
                      : item.value1}
                  </Typography>
                </Stack>
                <Box sx={{ minWidth: 120, textAlign: "center" }}>
                  {isYesNo ? (
                    <Box sx={classes.yesNoBox}>
                      <Typography
                        sx={{
                          color: isYes ? "#43D17A" : "#FF4C4C",
                          fontSize: 18,
                          letterSpacing: 1,
                        }}
                      >
                        {item.finalValue}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <LinearProgress
                        variant="determinate"
                        value={Number(item.finalValue)}
                        sx={{
                          height: 14,
                          borderRadius: 7,
                          background: "#e1e1e1",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: barColor,
                            borderRadius: 7,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          color: textColor,
                          fontWeight: 600,
                          mt: 0.5,
                        }}
                      >
                        {item.finalValue}%
                      </Typography>
                    </>
                  )}
                </Box>
                <Stack flex={1} spacing={0.5} alignItems="flex-end">
                  <Typography
                    sx={{ ...regularFont, color: "#FFF", opacity: 0.8 }}
                  >
                    {item.attribute2}
                  </Typography>
                  <Typography sx={{ ...regularFont, color: "#FFF" }}>
                    {Array.isArray(item.value2)
                      ? item.value2.join(", ")
                      : item.value2}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
        </Box>
      </Box>
    );
  };
  const getCompatibility = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.scrollStyle}>
                {getHeader()}
                {getAutocompletes()}
                {getButton()}
                {compatibilityData && (
                  <TwoUsersDetails
                    targetDetails={compatibilityData?.targetDetails}
                    userDetails={compatibilityData?.userDetails}
                  />
                )}
                {compatibilityPercentageData && (
                  <CompatibilityPercentage
                    percentageData={compatibilityPercentageData}
                  />
                )}
                {getTwoPersonCompatibilityList()}
              </Box>
            </Box>
          </Box>
        </Box>
        <CustomLoader isLoading={isLoading} />
      </>
    );
  };

  return getCompatibility();
};

export default Compatibility;
